import React, { useState } from "react";
import { Tooltip, Alert, Modal, Checkbox, Button } from "antd";
import { Box, LinearProgress} from "@mui/material";
import { POST_Remove_arquivo_context } from "../../../../../../../../../service/api";
import { toast } from "react-toastify";
import { FaRegFilePdf } from "react-icons/fa6";
import { IoDocumentTextOutline } from "react-icons/io5";
import { PiMicrosoftWordLogo } from "react-icons/pi";
import ModalUpload from "../../Update";
import { UploadDocumentoClientContextProps } from "../../../../../../../../../service/uploadService";
import {
  getFileIcon,
  limparNomeArquivo,
  pegarContadorInterno,
  pegarNumeroOS,
} from "../servicesTableCSS";
import styles from "./Tabelacontextdoc.module.css";

import { CiImport, CiTrash } from "react-icons/ci";

// Documentos ligados à pergunta de "Mudança de UF/Município" (A-JCM tipo 4)
// e ao par Destino/Origem da Inscrição Municipal (A-CCM tipo 4). Esses
// arquivos não carregam número de OS no nome, então a checagem normal por
// `numeroOS` nunca libera o botão de Remover pra eles.
const ARQUIVOS_MUDANCA_UF_MUNICIPIO = [
  "Constituicao-Contrato-Social-Destino",
  "Constituicao-Contrato-Social-Origem",
  "inscricao-municipal-destino",
  "inscricao-municipal-origem",
  "Constituicao-Contrato-Social"
];
const SITUACOES_PERMITEM_REMOVER_MUDANCA_UF_MUNICIPIO = ["A-JCM", "A-CCM"];

// Arquivos prioritários (aparecem primeiro na ordenação) — os cards desses
// arquivos recebem o destaque visual vermelho (faixa lateral + badge de OS)
// no Kanban. Isso NÃO afeta a cor do ícone, que segue sempre o tipo do
// arquivo (Word/PDF/neutro) — ver iconColorClass mais abaixo.
const arquivosPrioritarios = [
  "Constituicao-Contrato-Social",
  "Contrato-alteracao",
  "Alteracao-contratual",
  "Abertura-empresa",
];

export default function TabelaContextDoc({
  docsContexto,
  onDownload,
  FloatButtonDownload,
  FloatButtonDelete,
  loadingDocsContext,
  fetchData,
  dados,
  // Setters do estado de "Mudança de UF/Município", usados em
  // verificarAnaliseJuntaComercialAlteracao / verificarDocumentosCCM.
  // Precisam ser passados pelo componente pai que monta o `ctx` do
  // avancarOS — ver observação no final da resposta.
  setMudancaUF,
  setMudancaMunicipio,
  setEnderecoAntesDaMudancaUF,
  setImAntesDaMudanca,
}) {
  const temDocumentos = Array.isArray(docsContexto) && docsContexto.length > 0;

  const [isModalRemoverFile, setIsModalRemoverFile] = useState(false);
  const [fileToRemove, setFileToRemove] = useState(null);

  const [modalImportArquivo, setModalImportArquivo] = useState(false);
  const [nomeArquivoPadrao, setNomeArquivoPadrao] = useState("");

  // Seleção múltipla, exclusiva pros 5 arquivos de Mudança de UF/Município.
  // A exclusão em lote continua batendo na API um arquivo de cada vez
  // (loop sequencial com await), não existe endpoint de remoção em massa.
  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);
  const [isModalExcluirSelecionados, setIsModalExcluirSelecionados] = useState(false);
  const [excluindoSelecionados, setExcluindoSelecionados] = useState(false);

  if (!temDocumentos) {
    return (
      <div className={styles.emptyState}>
        <Alert message="Nenhum documento disponível." type="info" showIcon />
      </div>
    );
  }

  /**
   * Ao excluir um documento amarrado à decisão de Mudança de UF/Município,
   * reseta o(s) estado(s) correspondente(s) pra `null` — é isso que faz a
   * pergunta reaparecer da próxima vez que o usuário clicar em "Avançar" na
   * situação A-JCM. O reset acontece AQUI, no momento explícito da
   * exclusão, e não é inferido a partir de flags de retrocesso: assim não
   * existe ambiguidade entre "documento ainda não foi subido" (fluxo
   * normal, não deve resetar nada) e "documento foi apagado de propósito"
   * (deve resetar).
   *
   * Casos:
   * - Constituicao-Contrato-Social-Destino OU -Origem apagado → a resposta
   *   "Sim" para UF fica inválida: reseta mudancaUF, mudancaMunicipio e
   *   enderecoAntesDaMudancaUF.
   * - Constituicao-Contrato-Social (plano, sem sufixo) apagado → a
   *   resposta "Não" para UF fica inválida: reseta mudancaUF e
   *   mudancaMunicipio.
   * - inscricao-municipal-destino OU -origem apagado (situação A-CCM) →
   *   a resposta "Sim" para Município fica inválida: reseta
   *   mudancaMunicipio e imAntesDaMudanca.
   */
  const resetarDecisaoMudancaUFMunicipioSeNecessario = (fileName) => {
    const nomeSemExtensao = fileName.replace(/\.[^/.]+$/, "").toLowerCase();

    const eConstituicaoDestinoOuOrigem =
      nomeSemExtensao.includes("constituicao-contrato-social-destino") ||
      nomeSemExtensao.includes("constituicao-contrato-social-origem");

    const eConstituicaoPlano =
      nomeSemExtensao.includes("constituicao-contrato-social") && !eConstituicaoDestinoOuOrigem;

    const eInscricaoMunicipalDestinoOuOrigem =
      nomeSemExtensao.includes("inscricao-municipal-destino") ||
      nomeSemExtensao.includes("inscricao-municipal-origem");

    if (eConstituicaoDestinoOuOrigem) {
      setMudancaUF?.(null);
      setMudancaMunicipio?.(null);
      setEnderecoAntesDaMudancaUF?.(null);
    }

    if (eConstituicaoPlano) {
      setMudancaUF?.(null);
      setMudancaMunicipio?.(null);
    }

    if (eInscricaoMunicipalDestinoOuOrigem) {
      setImAntesDaMudanca?.(null);
    }
  };

  const confirmarExclusao = async () => {
    if (!fileToRemove) return;

    try {
      await POST_Remove_arquivo_context(
        `/cliente/arquivo/remover?filename=${encodeURIComponent(fileToRemove)}`
        // sem segundo argumento — não precisa de body
      );


      resetarDecisaoMudancaUFMunicipioSeNecessario(fileToRemove);
      await fetchData();
    } catch (error) {
      toast.error("Erro ao excluir arquivo.");
      console.error(error);
    } finally {
      setIsModalRemoverFile(false);
      setFileToRemove(null);
    }
  };

  // Marca/desmarca um arquivo na seleção pra exclusão em lote.
  const alternarSelecaoArquivo = (fileName, marcado) => {
    setArquivosSelecionados((prev) =>
      marcado ? [...prev, fileName] : prev.filter((nome) => nome !== fileName)
    );
  };

  // Exclui os arquivos marcados um de cada vez (await dentro do loop, sem
  // Promise.all) pra não estourar a API com chamadas simultâneas e pra
  // conseguir reportar exatamente qual arquivo falhou.
  const confirmarExclusaoSelecionados = async () => {
    if (arquivosSelecionados.length === 0) return;

    setExcluindoSelecionados(true);

    const falhas = [];

    for (const fileName of arquivosSelecionados) {
      try {
        await POST_Remove_arquivo_context(
          `/cliente/arquivo/remover?filename=${encodeURIComponent(fileName)}`
        );
        resetarDecisaoMudancaUFMunicipioSeNecessario(fileName);
      } catch (error) {
        falhas.push(fileName);
        console.error(error);
      }
    }

    await fetchData();

    if (falhas.length > 0) {
      toast.error(
        falhas.length === arquivosSelecionados.length
          ? "Erro ao excluir os arquivos selecionados."
          : `Erro ao excluir ${falhas.length} de ${arquivosSelecionados.length} arquivo(s).`
      );
    } else {
      toast.success("Arquivos excluídos com sucesso.");
    }

    setArquivosSelecionados([]);
    setExcluindoSelecionados(false);
    setIsModalExcluirSelecionados(false);
  };

  const abrirModalRemover = (fileName) => {
    const nomeSemExtensao = fileName.replace(/\.[^/.]+$/, "");

    if (nomeSemExtensao.includes("Alteracao-contratual")) {

      const nomeExibir = "Alteracao-contratual";
      Modal.confirm({
        title: "Arquivo já existe",
        content: (
          <>
            Deseja substituir o arquivo{" "}
            {nomeExibir === "Alteracao-contratual" ? (
              <strong>Modelo de Alteração de Contrato</strong>
            ) : (
              nomeExibir
            )}
            ?
          </>
        ),
        okText: "Sim",
        cancelText: "Não",
        onOk: async () => {
  try {
    await POST_Remove_arquivo_context(
      `/cliente/arquivo/remover?filename=${encodeURIComponent(fileName)}`
    );

    toast.success("Agora importe o novo arquivo para ser substituído");

    const match = nomeSemExtensao.match(/Alteracao-contratual-(\d{2})/);
    const contadorAtual = match ? parseInt(match[1], 10) : 0;
    const contadorNovo = String(contadorAtual + 1).padStart(2, "0");

    const novoNome = `Alteracao-contratual-${contadorNovo}`;
    setNomeArquivoPadrao(novoNome);
  } catch (error) {
    toast.error("Erro ao substituir arquivo.");
    console.error(error);
  } finally {
    setModalImportArquivo(true);
  }
},
      });
    } else {
      // Exclusão normal sem confirmação extra
      setFileToRemove(fileName);
      setIsModalRemoverFile(true);
    }
  };

  //coloca os arquivos PDF e WORD (arquivos que fazem parte do fluxo para cada tipo de Orden pelo Paralegal) no top do board
  const docsOrdenados = [...docsContexto].sort((a, b) => {
    const nomeA = limparNomeArquivo(a.nome);
    const nomeB = limparNomeArquivo(b.nome);

    const indexA = arquivosPrioritarios.indexOf(nomeA);
    const indexB = arquivosPrioritarios.indexOf(nomeB);

    // Se estiver na lista de prioritários, fica na frente
    if (indexA !== -1 && indexB === -1) return -1;
    if (indexA === -1 && indexB !== -1) return 1;

    // Se os dois estão na lista, mantém a ordem definida em arquivosPrioritarios
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;

    // Se nenhum está na lista, mantém a ordem original
    return 0;
  });

  return (
    <div className={styles.kanbanWrapper}>
      {arquivosSelecionados.length > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionCount}>
            {arquivosSelecionados.length} arquivo(s) selecionado(s)
          </span>
          <div className={styles.selectionActions}>
            <Button size="small" onClick={() => setArquivosSelecionados([])}>
              Limpar
            </Button>
            <Button
              size="small"
              danger
              type="primary"
              onClick={() => setIsModalExcluirSelecionados(true)}
            >
              Excluir selecionados
            </Button>
          </div>
        </div>
      )}

      {loadingDocsContext ? (
        <Box className={styles.loadingBox} sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      ) : (
        <div className={styles.kanbanBoard}>
          {docsOrdenados.map((file, index) => {
            const fileName = file.nome;
            const nomeLimpo = limparNomeArquivo(file.nome);
            const contador = pegarContadorInterno(fileName, nomeLimpo);
            const numeroOS = pegarNumeroOS(fileName, nomeLimpo);

            const descricao =
              nomeLimpo === "Contrato-alteracao"
                ? "- Contrato de alteração"
                : nomeLimpo === "Constituicao-Contrato-Social"
                  ? "- Constituição de Contrato Social"
                  : nomeLimpo === "Alteracao-contratual"
                    ? "- Constituição de Contrato Social"
                    : nomeLimpo === "Constituicao-Contrato-Social-Destino"
                      ? "- Constituição de Contrato Social - Destino"
                      : nomeLimpo === "Constituicao-Contrato-Social-Origem"
                        ? "- Constituição de Contrato Social - Origem"
                        : nomeLimpo === "Documento-certidao-conselho-classe"
                          ? "- Certidão do Conselho de Classe"
                          : nomeLimpo === "Abertura-empresa"
                            ? "- Modelo Contratual de Abertura da Empresa"
                            : nomeLimpo === "CNPJ"
                              ? "- CNPJ da Empresa"
                              : nomeLimpo === "inscricao-municipal-origem"
                                ? " - Inscrição Municipal - Origem"
                                : nomeLimpo === "inscricao-municipal-destino"
                                  ? "- Inscrição Municipal - Destino"
                                  : nomeLimpo === "inscricao-estadual"
                                    ? "- Inscrição Estadual"
                                    : nomeLimpo;

            const iconType = getFileIcon(nomeLimpo);
            const IconComponent =
              iconType === "word"
                ? PiMicrosoftWordLogo
                : iconType === "pdf"
                ? FaRegFilePdf
                : iconType === "cnpj"
                ? IoDocumentTextOutline
                : iconType === "im"
                ? IoDocumentTextOutline
                : IoDocumentTextOutline;

            // Cor do ícone segue o TIPO do arquivo — não a prioridade.
            // Assim um Word prioritário continua azul (Word), não vermelho.
            const iconColorClass =
              iconType === "word"
                ? styles.iconWrapperWord
                : iconType === "pdf"
                ? styles.iconWrapperPdf
                : styles.iconWrapperNeutral;

            // Libera o botão de Remover quando o arquivo pertence à OS atual
            // (regra original, via numeroOS) OU quando é um dos documentos de
            // Mudança de UF/Município e a OS está numa situação em que essa
            // exclusão precisa ser possível.
            const eArquivoMudancaUFMunicipio = ARQUIVOS_MUDANCA_UF_MUNICIPIO.includes(nomeLimpo);

            const podeRemover =
              numeroOS === String(dados.id) ||
              (eArquivoMudancaUFMunicipio &&
                SITUACOES_PERMITEM_REMOVER_MUDANCA_UF_MUNICIPIO.includes(dados.situacao?.id));

            // Checkbox de seleção em lote só aparece pros 5 arquivos de
            // Mudança de UF/Município, e só quando a remoção é permitida.
            const podeSelecionarParaExclusao = eArquivoMudancaUFMunicipio && podeRemover;

            // Destaque vermelho (faixa lateral + badge de OS): respeita a
            // lista de arquivos prioritários — não mexe na cor do ícone.
            const eArquivoPrioritario = arquivosPrioritarios.includes(nomeLimpo);
            const estaSelecionado = arquivosSelecionados.includes(fileName);

            const cardClassName = [
              styles.card,
              eArquivoPrioritario ? styles.cardPriority : "",
              estaSelecionado ? styles.cardSelected : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <div key={index} className={cardClassName}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIconGroup}>
                    <div className={`${styles.iconWrapper} ${iconColorClass}`}>
                      {IconComponent && <IconComponent />}
                    </div>
                    <div className={styles.cardTitleGroup}>
                      <span className={styles.cardTitle}>
                        {contador && (
                          <span className={styles.cardCounter}>{contador}° </span>
                        )}
                        {descricao}
                      </span>
                      {numeroOS && <span className={styles.osBadge}>OS: {numeroOS}</span>}
                    </div>
                  </div>

                  {podeSelecionarParaExclusao && (
                    <div className={styles.checkboxWrapper}>
                      <Checkbox
                        checked={estaSelecionado}
                        onChange={(e) =>
                          alternarSelecaoArquivo(fileName, e.target.checked)
                        }
                      />
                    </div>
                  )}
                </div>

                <div className={styles.cardMeta}>
                  <span>{file.modificado}</span>
                  <span className={styles.metaDot} />
                  <span>{file.tamanho}</span>

                   <Tooltip title="Download">
                    <div
                      className={styles.actionButton}
                      onClick={() => onDownload(fileName)}
                    >
                     <CiImport />
                    </div>
                  </Tooltip>

                  {podeRemover && (
                    <Tooltip title="Remover">
                      <div
                        className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                        onClick={() => abrirModalRemover(fileName)}
                      >
                        <CiTrash />
                      </div>
                    </Tooltip>
                  )}
                </div>

                <div className={styles.cardFooter}>
                  {nomeLimpo === "Constituicao-Contrato-Social-Origem" ? (
                    <div>
                      <span className={styles.info}>
                        Houve uma alteração de UF. Dessa forma, foi solicitada a <strong>Constituição do Contrato Social de Origem</strong> para que a alteração seja devidamente registrada e abatida no sistema.
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        title="Confirmar exclusão"
        open={isModalRemoverFile}
        onOk={confirmarExclusao}
        onCancel={() => setIsModalRemoverFile(false)}
        okText="Excluir"
        cancelText="Cancelar"
      >
        <p>
          Tem certeza que deseja excluir o arquivo{" "}
          <strong>{fileToRemove}</strong>?
        </p>
      </Modal>

      <Modal
        title="Confirmar exclusão em lote"
        open={isModalExcluirSelecionados}
        onOk={confirmarExclusaoSelecionados}
        onCancel={() => setIsModalExcluirSelecionados(false)}
        okText="Excluir"
        cancelText="Cancelar"
        confirmLoading={excluindoSelecionados}
      >
        <p>Tem certeza que deseja excluir os arquivos selecionados?</p>
        <ul>
          {arquivosSelecionados.map((nome) => (
            <li key={nome}>{nome}</li>
          ))}
        </ul>
      </Modal>

      {modalImportArquivo && (
        <ModalUpload
          onClose={() => setModalImportArquivo(false)}
          uploadProps={{
            ...UploadDocumentoClientContextProps(null, "/upload"),
            beforeUpload: (file) => {
              const extensao = file.name.substring(file.name.lastIndexOf("."));
              return new File([file], `${nomeArquivoPadrao}${extensao}`, {
                type: file.type,
              });
            },
            onChange: ({ file }) => {
              if (file.status === "done") {
                fetchData(); // só chama quando terminar
              }
            },
          }}
        />
      )}
    </div>
  );
}